import { isHotkeyPressed } from 'react-hotkeys-hook';

import { toggle } from 'radash';

import { diagram } from '@diagram/state/diagram';
import type { SelectionItem } from '@diagram/state/selection';
import { derived, selectionViewfinderState, state } from '@diagram/state/selection';

const BaseSelectionService = {
    setStart: (point: (typeof selectionViewfinderState)['start']) => {
        selectionViewfinderState.start = point;
    },

    setEnd: (point: (typeof selectionViewfinderState)['end']) => {
        selectionViewfinderState.end = point;
    },

    select: (itemOrItems: SelectionItem | SelectionItem[]) => {
        state.selection = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
    },

    selectAll: () => {
        const items: SelectionItem[] = [];

        Object.values(diagram.componentInstances).forEach((componentInstance) => {
            items.push({
                type: 'component-instance',
                id: componentInstance.id,
            });
        });

        Object.values(diagram.connections).forEach((connection) => {
            items.push({
                type: 'connection',
                id: connection.id,
            });
        });

        Object.values(diagram.images).forEach((image) => {
            items.push({
                type: 'image',
                id: image.id,
            });
        });

        Object.values(diagram.groups).forEach((group) => {
            items.push({
                type: 'group',
                id: group.id,
            });
        });

        Object.values(diagram.textareas).forEach((textarea) => {
            items.push({
                type: 'textarea',
                id: textarea.id,
            });
        });

        state.selection = items;
    },

    toggle: (item: SelectionItem) => {
        const stringify = (item: SelectionItem) => {
            return `${item.type}:${item.id}`;
        };

        state.selection = toggle(state.selection, item, stringify);
    },

    selectOrToggle: (item: SelectionItem) => {
        if (isHotkeyPressed('shift') || isHotkeyPressed('alt') || isHotkeyPressed('meta')) {
            SelectionService.toggle(item);

            return {
                selected: false,
                toggled: true,
            };
        } else {
            SelectionService.select(item);

            return {
                selected: true,
                toggled: false,
            };
        }
    },

    isSelected: (item: SelectionItem) => {
        return derived.getSelectedIds[item.id];
    },

    isOnlySelected: (item: SelectionItem) => {
        return derived.getOnlySelectedId === item.id;
    },

    getSelectedItemsByType: (type: SelectionItem['type']) => {
        const { selection } = state;

        return selection.filter((item) => item.type === type).map((item) => item.id);
    },

    getSelectedItems: () => {
        const { selection } = state;

        return selection;
    },

    clear: () => {
        state.selection = [];
    },
};

type Singulars = 'ComponentInstance' | 'Connection' | 'Group' | 'Image' | 'Textarea' | 'Comment';
type Plurals = 'ComponentInstances' | 'Connections' | 'Groups' | 'Images' | 'Textareas' | 'Comments';

type GeneratedMethods = {
    [Plural in Plurals as `getSelected${Plural}`]: () => string[];
} & {
    [Singular in Singulars as `select${Singular}`]: (id: string) => void;
} & {
    [Plural in Plurals as `select${Plural}`]: (ids: string[]) => void;
} & {
    [Singular in Singulars as `selectOrToggle${Singular}`]: (id: string) => {
        selected: boolean;
        toggled: boolean;
    };
} & {
    [Singular in Singulars as `isSelected${Singular}`]: (id: string) => boolean;
} & {
    [Singular in Singulars as `isOnlySelected${Singular}`]: (id: string) => boolean;
};

const SelectionService = {
    ...BaseSelectionService,
} as typeof BaseSelectionService & GeneratedMethods;

const elements = [
    { singular: 'ComponentInstance', plural: 'ComponentInstances', value: 'component-instance' },
    { singular: 'Connection', plural: 'Connections', value: 'connection' },
    { singular: 'Group', plural: 'Groups', value: 'group' },
    { singular: 'Image', plural: 'Images', value: 'image' },
    { singular: 'Textarea', plural: 'Textareas', value: 'textarea' },
    { singular: 'Comment', plural: 'Comments', value: 'comment' },
] as const;

elements.forEach((type) => {
    SelectionService[`getSelected${type.plural}`] = () => {
        return SelectionService.getSelectedItemsByType(type.value);
    };

    SelectionService[`select${type.singular}`] = (id: string) => {
        return SelectionService.select({
            type: type.value,
            id,
        });
    };

    SelectionService[`select${type.plural}`] = (ids: string[]) => {
        const items = ids.map((id) => ({
            type: type.value,
            id,
        }));

        return SelectionService.select(items);
    };

    SelectionService[`selectOrToggle${type.singular}`] = (id: string) => {
        return SelectionService.selectOrToggle({
            type: type.value,
            id,
        });
    };

    SelectionService[`isSelected${type.singular}`] = (id: string) => {
        return SelectionService.isSelected({
            type: type.value,
            id,
        });
    };

    SelectionService[`isOnlySelected${type.singular}`] = (id: string) => {
        return SelectionService.isOnlySelected({
            type: type.value,
            id,
        });
    };
});

export { SelectionService };
